import { Box, CircularProgress, Paper, Typography } from '@mui/material'
import Container from '@mui/material/Container'
import React, { useState } from 'react'
import * as Yup from 'yup'

import {
  Event as EventApi,
  FormEvent as FormEventApi,
  FormParticipant as FormParticipantApi,
  Participant,
} from 'API'
import Page from 'components/elements/Page'
import EmptyState from 'components/EmptyState'
import Section from 'components/Section'
import { listParticipantsByEvent } from 'hooks/listParticipantsByEvent'
import {
  listParticipantsSummaryByEvent,
  listParticipantsShirtSummaryByEvent,
} from 'hooks/listParticipantsSummaryByEvent'
import {
  useEventsForDownload,
  getEventForDownload,
} from 'hooks/useEventsForDownload'

import Form from './form'
import schema from './form/schema'
import {
  convertToSheetData,
  generateSpreadSheet,
  generateSummary,
} from './utils'

interface Props {}

type Event = Omit<Exclude<EventApi, null>, '__typename'> & {
  isFP?: boolean
}
type FormEvent = Omit<Exclude<FormEventApi, null>, '__typename'> & {
  isFP?: boolean
}
type FormParticipant = Omit<Exclude<FormParticipantApi, null>, '__typename'> & {
  tshirtCountTotal: number
}

export enum RequestType {
  DOWNLOAD = 'download',
  SHOW_PARTICIPANT_SUMMARY = 'showParticipantSummary',
  SHOW_SHIRT_SUMMARY = 'showShirtSummary',
}

const DownloadParticipants: React.FC<Props> = () => {
  const [isGenerating, setIsGenerating] = useState(false)

  const { events, apiLoading, apiError } = useEventsForDownload()
  const [summary, setSummary] = useState(null)

  const handleSubmit = async (
    values: Yup.InferType<typeof schema>,
    type: RequestType,
  ) => {
    setIsGenerating(true)
    let selectedEvent =
      events.filter(
        (event: Event | FormEvent) => event.id === values.eventId,
      )?.[0] || null

    if (!selectedEvent)
      selectedEvent = await getEventForDownload(values.eventId.toLowerCase())

    if (selectedEvent) {
      let results = null
      let sheetData = null
      if (type !== RequestType.DOWNLOAD) {
        results =
          type === RequestType.SHOW_PARTICIPANT_SUMMARY
            ? await listParticipantsSummaryByEvent({
                eventId: selectedEvent.id,
                isFormParticipant: !!(selectedEvent as FormEvent).isFP,
              })
            : await listParticipantsShirtSummaryByEvent({
                eventId: selectedEvent.id,
                isFormParticipant: !!(selectedEvent as FormEvent).isFP,
              })

        sheetData = convertToSheetData(
          selectedEvent.id,
          results.participants as Participant[] | FormParticipant[],
        )

        const summary = generateSummary({
          event: selectedEvent,
          type:
            type === RequestType.SHOW_PARTICIPANT_SUMMARY
              ? 'participant'
              : 'shirt',
          data: sheetData,
        })
        setSummary(summary)
      } else {
        results = await listParticipantsByEvent({
          eventId: selectedEvent.id,
          isFormParticipant: !!(selectedEvent as FormEvent).isFP,
        })
        sheetData = convertToSheetData(
          selectedEvent.id,
          results.participants as Participant[] | FormParticipant[],
        )
        generateSpreadSheet({
          event: selectedEvent,
          isFP: !!(selectedEvent as FormEvent).isFP,
          data: sheetData,
          formShirtData: results.shirtTotal,
        })
      }
    }
    setIsGenerating(false)
  }

  return (
    <Section>
      <Page noPadding>
        <Container maxWidth="sm">
          <div style={{ textAlign: 'center' }}>
            {apiLoading && <CircularProgress />}
            {apiError && (
              <EmptyState
                header={apiError.title}
                text={apiError.message}></EmptyState>
            )}
          </div>

          {!apiLoading && (
            <Form
              onSubmit={handleSubmit}
              events={events}
              loading={apiLoading || isGenerating}
            />
          )}
          {!apiLoading && Object.keys(summary ?? {}).length > 0 && (
            <Paper sx={{ pb: 1, px: 3 }}>
              <Typography variant="overline" sx={{ mb: 4, pb: 4 }}>
                {summary.title}
              </Typography>
              {summary.data.map((item) => (
                <Box sx={{ mt: 2, p: 1, bgcolor: '#D1E5C9' }} key={item.name}>
                  <Typography key={item.name} sx={{ fontWeight: 'bold' }}>
                    {item.name}
                  </Typography>
                  <>
                    {item.rows?.map((row) => (
                      <Box
                        key={`${item.name}-${row.name}`}
                        sx={{
                          mx: 2,
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}>
                        <Typography
                          key={`${item.name}-${row.name}-name`}
                          variant="body2"
                          sx={{
                            fontWeight:
                              row.name.toLowerCase() === 'total'
                                ? 'bold'
                                : 'normal',
                          }}>
                          {row.name}
                        </Typography>
                        <Typography
                          key={`${item.name}-${row.name}-total`}
                          variant="body2"
                          sx={{
                            fontWeight:
                              row.name.toLowerCase() === 'total'
                                ? 'bold'
                                : 'normal',
                          }}>
                          {row.count}
                        </Typography>
                      </Box>
                    ))}
                  </>
                </Box>
              ))}
            </Paper>
          )}
        </Container>
      </Page>
    </Section>
  )
}

export default DownloadParticipants
